import { useEffect, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import { isFeatureEnabled, waitTillAvailable } from '@vp/ab-reader';
import { bugtracker, useIdentityContext } from '@99designs/design-services-common';
import { FullPageSpinner } from '@99designs/design-services-layouts';
import { useProLogoDesignFeatureFlag } from '@99designs/experiments';
import {
    ServiceProductOptions,
    determineActualEsMpvID,
    determineActualProductKey,
    getRequestExperiments,
    useCreateBriefMutation,
    useCreateRequestMutation,
    useIntelligentStylesExperiment,
    useLogoTouchUpFeatureFlag,
    useNavigateWithLang,
    useWeddingExperiment,
} from '@99designs/feature-design-services-brief';
import { KeyValue } from '@99designs/graph-utils/types';
import { DS_BRIEF_MULTI_STEP_URL } from '@99designs/i18n';

export async function getIsIntelligentBriefsFeatureFlagEnabled(
    serviceMpvId: string | null,
    printMpvId: string | null,
    workId: string | null
) {
    if (
        serviceMpvId === 'careDesign' &&
        !workId &&
        printMpvId &&
        printMpvId.toLocaleLowerCase() !== 'weddinginvitations'
    ) {
        const available = await waitTillAvailable(3000);
        if (!available) {
            Bugsnag.notify(new Error('AB reader is not available'));
            return false;
        }
        return isFeatureEnabled('expert_services_intelligent_briefs');
    }

    return false;
}

const getQueryParamClosure =
    (query: URLSearchParams) =>
    (...keys: string[]): string[] => {
        const entries = Array.from(query.entries());
        const foundEntries: string[] = [];
        for (const key of keys) {
            const foundEntry = entries.find(([k]) => k.toLowerCase() === key.toLowerCase());
            if (foundEntry) {
                return foundEntries.concat(foundEntry[1]);
            }
        }
        return foundEntries;
    };

// Override logic only is relevant for the Logo feature gates when users are not enrolled.

export const Create: React.FC<{ locale: string }> = ({ locale }) => {
    const getQueryParam = getQueryParamClosure(new URLSearchParams(window.location.search));

    const mpvId = getQueryParam('mpvId').pop() ?? '';
    const esMpvID = getQueryParam('esmpvId').pop() ?? '';
    const workId = getQueryParam('workId').pop() ?? null;
    const options = getQueryParam('options', 'selectedOptions').pop() ?? null;
    const quantity = getQueryParam('quantity', 'qty').pop();
    const title = getQueryParam('title').pop() ?? undefined;
    const requestType = getQueryParam('requestType').pop();
    const schemaId = getQueryParam('schemaId').pop() ?? undefined;
    const isTest = getQueryParam('isTest').pop() === 'true';
    const brief_experiments = getQueryParam('brief_experiments').pop() ?? undefined;
    const referenceProjects = getQueryParam('referenceProjects').pop() ?? undefined;
    const productKey = getQueryParam('productKey').pop() ?? '';
    const intelligentBriefs = getQueryParam('ib').pop() ?? undefined;
    const serviceProductOptions = new ServiceProductOptions(
        getQueryParam('serviceselectedoptions').pop()
    );

    // @deprecated: use {@link logomakerEntityId} and {@link logomakerEntitySig} instead
    const logomakerDesigns = getQueryParam('logomakerDesigns').pop() ?? undefined;
    const logomakerEntityId = getQueryParam('logomakerEntityId').pop() ?? undefined;
    const logomakerEntitySig = getQueryParam('logomakerEntitySig').pop() ?? undefined;

    const [hasIntelligentBriefs, setHasIntelligentBriefs] = useState<boolean | null>(
        intelligentBriefs?.toLowerCase() === 'false' ? false : null
    );

    const { isFeatureGateEnabled: isProLogoDesignEnabled, isLoading: isProLogoDesignLoading } =
        useProLogoDesignFeatureFlag(esMpvID);
    const { isFeatureGateEnabled: isLogoTouchUpEnabled, isLoading: isLogoTouchUpLoading } =
        useLogoTouchUpFeatureFlag(esMpvID);
    const { isEnabled: isIntelligentStylesEnabled, isLoading: isIntelligentStylesLoading } =
        useIntelligentStylesExperiment({ locale, esMpvID, printMpvID: mpvId });

    useEffect(() => {
        if (hasIntelligentBriefs === null) {
            getIsIntelligentBriefsFeatureFlagEnabled(esMpvID, mpvId, workId).then(
                setHasIntelligentBriefs
            );
        }
    }, [esMpvID, hasIntelligentBriefs, mpvId, workId]);

    let initFields: { [key: string]: string } = {};
    if (mpvId !== '') {
        initFields = { ...initFields, physicalProduct: mpvId };
    }

    if (requestType === 'DesignLive' || requestType === 'Standard') {
        initFields = { ...initFields, requestType };
    }
    if (workId) {
        initFields = { ...initFields, workId };
    }
    if (title) {
        initFields = { ...initFields, title };
    }

    // TODO: remove, these no longer need to go into "initFields" when all logomaker goes through
    //       the new (circa late 2025) product flow

    if (logomakerDesigns) {
        initFields = { ...initFields, logomakerDesigns };
    }

    if (
        hasIntelligentBriefs === null ||
        isIntelligentStylesLoading ||
        isProLogoDesignLoading ||
        isLogoTouchUpLoading
    ) {
        return null;
    }

    const actualEsMpvID = determineActualEsMpvID(
        esMpvID,
        isLogoTouchUpEnabled || isProLogoDesignEnabled
    );
    const actualProductKey = determineActualProductKey(
        esMpvID,
        productKey,
        isLogoTouchUpEnabled || isProLogoDesignEnabled
    );

    // TODO: isRnBEnabled is legacy. In the near future, product keys will not be sent in favour
    //       of the ES MPV ID.

    const isRnBEnabled = ![
        'logo-essential',
        'logo-touch-up-&-completion-service',
        'essential-stand-up-pouch-package',
    ].includes(actualProductKey);

    return (
        <ClientNewRequest
            mpvId={mpvId}
            experiments={{
                isIntelligentBriefsEnabled: hasIntelligentBriefs,
                isIntelligentStylesEnabled,
                schemaId,
                referenceProjectsEnabled: referenceProjects === 'true' ? true : undefined,
            }}
            params={
                isRnBEnabled
                    ? {
                          type: 'create_request',
                          esmpvId: actualEsMpvID,
                          workId: workId,
                          withDesignLive: requestType === 'DesignLive',
                          logomakerDesigns: logomakerDesigns,
                          logomakerEntityId: logomakerEntityId,
                          logomakerEntitySig: logomakerEntitySig,
                          title: title,
                          options: options,
                          locale: locale,
                          quantity: quantity ? Number(quantity) : null,
                          serviceProductOptions: serviceProductOptions.asArray,
                          isTest: isTest,
                      }
                    : {
                          type: 'create_brief',
                          productKey:
                              actualProductKey ||
                              (() => {
                                  throw new Error('productKey is required');
                              })(),
                          fields: initFields,
                      }
            }
            router={{
                query: {
                    brief_experiments,
                },
            }}
        />
    );
};

export interface ClientNewRequestProps {
    params:
        | {
              type: 'create_request';
              esmpvId: string;
              workId: string | null;
              options: string | null;
              quantity: number | null;
              locale: string;
              withDesignLive?: boolean;
              title?: string;
              // @deprecated: use {@link logomakerEntityId} and {@link logomakerEntitySig} instead
              logomakerDesigns?: string;
              logomakerEntityId?: string;
              logomakerEntitySig?: string;
              serviceProductOptions?: KeyValue[];
              isTest?: boolean;
          }
        | {
              type: 'create_brief';
              productKey: string;
              fields: {
                  [key: string]: string;
              };
          };
    mpvId: string | null;
    experiments: {
        isIntelligentBriefsEnabled: boolean;
        isIntelligentStylesEnabled: boolean;
        schemaId?: string;
        referenceProjectsEnabled: boolean | undefined;
    };
    router: {
        query: { [key: string]: string | string[] | undefined };
    };
}

export function ClientNewRequest({
    mpvId,
    experiments: {
        isIntelligentBriefsEnabled,
        isIntelligentStylesEnabled,
        schemaId,
        referenceProjectsEnabled,
    },
    params,
}: ClientNewRequestProps) {
    const navigate = useNavigateWithLang();
    const [called, setCalled] = useState(false);
    const { accessToken } = useIdentityContext();
    const [createBrief] = useCreateBriefMutation({
        onCompleted: async (data) => {
            navigate(`${DS_BRIEF_MULTI_STEP_URL}/${data.brief.id}`);
        },
        onError: (e) => {
            bugtracker.notify(e);
            navigate(DS_BRIEF_MULTI_STEP_URL + '/error');
        },
        errorPolicy: 'all',
    });
    const [createRequest] = useCreateRequestMutation({
        onCompleted: (data) => {
            const currentUrl = data.createRequest.facts?.currentUrl;
            if (currentUrl) {
                return navigate(currentUrl, { replace: true });
            }
            return navigate(DS_BRIEF_MULTI_STEP_URL + '/error');
        },
        onError: (e) => {
            bugtracker.notify(e);
            navigate(DS_BRIEF_MULTI_STEP_URL + '/error');
        },
        errorPolicy: 'all',
    });

    const isWeddingBriefEnabled = useWeddingExperiment(mpvId);

    useEffect(() => {
        if (accessToken === '') {
            return;
        }
        if (called) {
            return;
        }
        setCalled(true);
        if (params.type === 'create_brief') {
            createBrief({ variables: { slug: params.productKey, fields: params.fields } });
            return;
        }

        const isTest = params.isTest === true ? true : undefined;

        void createRequest({
            variables: {
                input: {
                    facts: {
                        locale: params.locale,
                        expertService: {
                            mpvId: params.esmpvId,
                            withDesignLive: params.withDesignLive ?? false,
                        },
                    },
                    optionalFacts: {
                        workId: params.workId ?? undefined,
                        experiments: getRequestExperiments(
                            isIntelligentBriefsEnabled,
                            isWeddingBriefEnabled,
                            isIntelligentStylesEnabled,
                            schemaId,
                            referenceProjectsEnabled
                        ),
                        title: params.title,
                        logomakerDesigns: params.logomakerDesigns,
                        logomakerEntityId: params.logomakerEntityId,
                        logomakerEntitySig: params.logomakerEntitySig,
                        printOutcome: {
                            mpvId,
                            options: params.options ? { encoded: params.options } : undefined,
                            quantity: params.quantity ?? 1,
                        },
                        serviceProductOptions: params.serviceProductOptions,
                        isTest: isTest,
                    },
                },
            },
        });
    }, [
        createRequest,
        mpvId,
        accessToken,
        createBrief,
        called,
        params,
        isIntelligentBriefsEnabled,
        isWeddingBriefEnabled,
        isIntelligentStylesEnabled,
        schemaId,
        referenceProjectsEnabled,
    ]);

    return <FullPageSpinner />;
}
